import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtCFA } from "@/lib/format";
import { useTheme } from "@/context/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Banknote, AlertTriangle, CalendarClock, Wrench, Package, Contact, UserCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { theme } = useTheme();
  const dark = theme === "dark";

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="text-muted-foreground text-sm animate-pulse" data-testid="dashboard-loading">Chargement du tableau de bord…</div>;
  }

  const stats = [
    {
      label: "Ventes du jour",
      value: fmtCFA(data.ventes_jour.total),
      sub: `${data.ventes_jour.nb_ventes} transaction(s) — Caisse ${data.caisse_statut === "ouverte" ? "ouverte" : "fermée"}`,
      icon: Banknote,
      color: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-500/10 border-emerald-500/30",
      testid: "stat-ventes-jour",
    },
    {
      label: "Alertes stock bas",
      value: data.stock_bas.length,
      sub: "produit(s) sous le seuil",
      icon: AlertTriangle,
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-500/10 border-amber-500/30",
      testid: "stat-stock-bas",
    },
    {
      label: "Présences aujourd'hui",
      value: data.presences_jour,
      sub: "passages à l'entrée",
      icon: UserCheck,
      color: "text-purple-600 dark:text-purple-400",
      bg: "bg-purple-500/10 border-purple-500/30",
      testid: "stat-presences",
    },
    {
      label: "Abonnements à renouveler",
      value: data.nb_membres_a_renouveler,
      sub: `sur ${data.nb_membres} membre(s)`,
      icon: Contact,
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-500/10 border-amber-500/30",
      testid: "stat-membres",
    },
    {
      label: "Séances au catalogue",
      value: data.nb_seances,
      sub: "séances disponibles",
      icon: CalendarClock,
      color: "text-blue-600 dark:text-blue-400",
      bg: "bg-blue-500/10 border-blue-500/30",
      testid: "stat-seances",
    },
    {
      label: "Équipements en maintenance",
      value: data.equipements_maintenance,
      sub: `sur ${data.nb_equipements} équipements`,
      icon: Wrench,
      color: "text-red-600 dark:text-red-400",
      bg: "bg-red-500/10 border-red-500/30",
      testid: "stat-maintenance",
    },
  ];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Tableau de bord</h1>
        <p className="text-sm text-muted-foreground mt-1">Vue d'ensemble de votre salle de sport aujourd'hui</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="bg-card border-border" data-testid={s.testid}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{s.label}</p>
                  <p className={`text-2xl font-bold mt-2 ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${s.bg}`}>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="bg-card border-border xl:col-span-2" data-testid="chart-revenue-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-foreground">Revenus des 7 derniers jours</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              <BarChart data={data.semaine}>
                <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1E293B" : "#E2E8F0"} />
                <XAxis dataKey="jour" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: dark ? "#131B2E" : "#FFFFFF", border: `1px solid ${dark ? "#1E293B" : "#E2E8F0"}`, borderRadius: 8 }}
                  labelStyle={{ color: dark ? "#F8FAFC" : "#0F172A" }}
                  formatter={(v) => [fmtCFA(v), "Total"]}
                />
                <Bar dataKey="total" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="low-stock-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
              <Package className="w-4 h-4 text-amber-600 dark:text-amber-400" /> Alertes stock bas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.stock_bas.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="low-stock-empty">Aucune alerte. Tous les stocks sont sains.</p>
            )}
            {data.stock_bas.map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-background border border-border rounded-lg px-3 py-2.5" data-testid={`low-stock-item-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{p.nom}</p>
                  <p className="text-xs text-muted-foreground">Seuil : {p.seuil_alerte}</p>
                </div>
                <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30 shrink-0">
                  Stock : {p.stock}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border" data-testid="renouvellements-card">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
            <Contact className="w-4 h-4 text-amber-600 dark:text-amber-400" /> Abonnements à renouveler
            <Link to="/membres" className="ml-auto text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline" data-testid="renouvellements-link">Voir les membres</Link>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.membres_a_renouveler.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="renouvellements-empty">Aucun abonnement expiré ou proche de l'échéance.</p>
          )}
          {data.membres_a_renouveler.map((m) => (
            <div key={m.id} className="flex items-center justify-between bg-background border border-border rounded-lg px-3 py-2.5" data-testid={`renouvellement-item-${m.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{m.prenom} {m.nom}</p>
                <p className="text-xs text-muted-foreground">{m.telephone} · {m.abonnement}</p>
              </div>
              <Badge className={m.statut === "expire" ? "bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30 shrink-0" : "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 shrink-0"}>
                {m.statut === "expire" ? `Expiré depuis ${-m.jours_restants} j` : `Expire dans ${m.jours_restants} j`}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
